/**
 * NetWorthSection — asset / liability composition charts on Accounts > Overview.
 *
 * Phase 86: this component no longer calculates anything. It used to own a
 * second set of Assets / Liabilities / Net-worth KPI cards computed from its own
 * fetches, which disagreed with the hero above it by ~₹2L for real users. The
 * headline numbers now live solely in PositionHero; this section is purely the
 * composition view, and its slices are read straight from the canonical
 * `position.composition` so they always add up to the headline totals.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { formatMoney } from '@/lib/format';

const ASSET_SLICES = [
  ['accounts_assets', 'Bank & cash'],
  ['deposits_assets', 'Deposits (FD / RD)'],
  ['holdings_assets', 'Investments'],
];
const LIABILITY_SLICES = [
  ['accounts_liabilities', 'Credit cards'],
  ['holdings_liabilities', 'Loans'],
];

function tooltipStyle() {
  return {
    borderRadius: 12,
    border: '1px solid hsl(var(--border))',
    background: 'hsl(var(--popover))',
    color: 'hsl(var(--popover-foreground))',
    fontSize: 12,
  };
}

function EmptyMini({ label }) {
  return (
    <div
      className="h-56 rounded-xl border border-dashed border-border/70 grid place-items-center text-xs text-muted-foreground p-4 text-center"
      data-testid="networth-mini-empty"
    >
      {label}
    </div>
  );
}

export default function NetWorthSection({ position = null, baseCurrency = 'INR' }) {
  const base = position?.base_currency || baseCurrency;
  const composition = position?.composition || {};

  const assetData = useMemo(() => ASSET_SLICES
    .map(([key, name]) => ({ name, value: Number(composition[key] || 0) }))
    .filter((s) => s.value > 0), [composition]);

  const liabData = useMemo(() => LIABILITY_SLICES
    .map(([key, name]) => ({ name, value: Number(composition[key] || 0) }))
    .filter((s) => s.value > 0), [composition]);

  const overview = useMemo(() => ([
    { name: 'Assets',      value: position?.assets || 0 },
    { name: 'Liabilities', value: position?.liabilities || 0 },
  ]), [position]);

  if (!position) return null;
  if (assetData.length === 0 && liabData.length === 0) return null;

  return (
    <section className="space-y-4" data-testid="accounts-networth-section">
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Assets vs Liabilities */}
        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-5">
            <div className="text-sm font-medium mb-3">Assets vs liabilities</div>
            <div className="h-56" data-testid="networth-bar-avl">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={overview} layout="vertical" margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="hsl(var(--border))" opacity={0.35} horizontal={false} />
                  <XAxis type="number" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} axisLine={false} tickLine={false} width={80} />
                  <RTooltip formatter={(v) => formatMoney(v, base)} contentStyle={tooltipStyle()} />
                  <Bar dataKey="value" radius={[8, 8, 8, 8]}>
                    {overview.map((row, i) => (
                      <Cell key={i} fill={row.name === 'Assets' ? 'hsl(var(--chart-5))' : 'hsl(var(--chart-1))'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Asset mix */}
        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-5 flex flex-col h-full">
            <div className="text-sm font-medium mb-3">Asset mix</div>
            {assetData.length === 0 ? (
              <EmptyMini label="Add assets to see your mix." />
            ) : (
              <div className="h-56" data-testid="networth-donut-assets">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={assetData} dataKey="value" innerRadius={48} outerRadius={78} paddingAngle={2}>
                      {assetData.map((c, i) => (
                        <Cell key={c.name} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />
                      ))}
                    </Pie>
                    <RTooltip formatter={(v) => formatMoney(v, base)} contentStyle={tooltipStyle()} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-border/50">
              <Link
                to="/accounts?tab=investments"
                className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
                data-testid="networth-see-more-assets"
              >
                See more <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Liability mix */}
        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-5 flex flex-col h-full">
            <div className="text-sm font-medium mb-3">Liability mix</div>
            {liabData.length === 0 ? (
              <EmptyMini label="No liabilities. A strong position." />
            ) : (
              <div className="h-56" data-testid="networth-donut-liabilities">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={liabData} dataKey="value" innerRadius={48} outerRadius={78} paddingAngle={2}>
                      {liabData.map((c, i) => (
                        <Cell key={c.name} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />
                      ))}
                    </Pie>
                    <RTooltip formatter={(v) => formatMoney(v, base)} contentStyle={tooltipStyle()} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-border/50">
              <Link
                to="/accounts?tab=liabilities"
                className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
                data-testid="networth-see-more-liabilities"
              >
                See more <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {position.liability_estimates?.applied > 0 && (
        <p className="text-[11px] text-muted-foreground" data-testid="networth-liability-estimate-note">
          Liabilities include {position.liability_estimates.applied} 2120-estimated loan
          balance{position.liability_estimates.applied === 1 ? '' : 's'}
          {position.liability_estimates.as_of
            ? ` as of ${new Date(position.liability_estimates.as_of)
                .toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`
            : ''}.
        </p>
      )}
    </section>
  );
}
